import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  ContractKeySchema,
  ENDPOINTS,
  baseContractCandidateOf,
  baseContractIneligibility,
  type BaseContractNomination,
  type BaseContractType,
  type BaseContractTypeEntry,
  type ContractDetail,
  type DecideContractTypeResponse,
  type ListBaseContractsResponse,
} from '@clm/shared-types';

import { listBaseContracts, nominateBaseContract } from '../api/base-contracts';
import { retrieveContract } from '../api/contracts';
import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { mayAttempt } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { ContractTypeTag } from '../components/ContractTypeTag';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { ErrorNotice } from '../components/ErrorNotice';
import { requireSurface } from '../routes/surfaces';
import {
  BASE_MSA_FORBIDDEN_COPY,
  BASE_MSA_READ_ONLY_COPY,
  BASE_NOMINATOR_ROLES,
  INELIGIBLE_COPY,
  nominationProblemOf,
  scopedWithoutBaseCopy,
  type NominationProblem,
} from './base-msa-shared';
import { confirmTypeAndSetBase, confirmsTypeFirst } from './base-msa-confirm';
import { NominationProblemNotice } from './BaseMsaRefusal';
import { DecisionProblemNotice } from './ContractTypeDecisionPanel';
import { receiptLine, type DecisionProblem } from './contract-type-decision';
import { PanelProblem } from './quality/panel';
import { usePanel } from './quality/panel-state';

/**
 * Admin **Base MSA** — CLM-111 (FR-37, DR-11; R-111-1 … R-111-9).
 *
 * Which signed contract is cloudEQ's base MSA, and every earlier one: who
 * nominated each version and when, who replaced it and when, and why. One
 * read, `GET /v1/admin/base-contracts` (`clause:review_match` — Admin and
 * Legal, D-2.1-11), scoped on the API side; one write,
 * `POST /v1/admin/base-contracts/nominate` (`admin:configure` — Admin only).
 *
 * ## Nominating is preview-then-confirm
 *
 * An admin types a contract key and previews it through `retrieve-contract`
 * — the same scoped read the contract record uses, so a key the admin cannot
 * see is the same one-sentence 404 there as here. The preview names the
 * contract and applies `baseContractIneligibility`, the rule the API refuses
 * on, so a Draft, a superseded agreement, a non-MSA, a provisional type
 * (CLM-110, R-110-21: confirm the type first), an amendment or a
 * processing-restricted contract is explained before anything is sent. Either
 * key shape previews (`SGW-0012`, or `SGW-MSA-0003` issued before D-110-1).
 * Confirming posts the nomination with the **current nomination this page
 * showed** as `expected_current_nomination_id`: if another admin replaced the
 * base in the meantime the API answers `409 base_changed`, and the page reloads
 * the list rather than overwriting a decision nobody here has seen.
 *
 * **An MSA confirmed because the file name and the AI agree** (CLM-118,
 * R-118-13) is not yet a base: no administrator has checked its type
 * (`type_not_admin_confirmed`). The preview offers **Confirm type and set as
 * base**, which posts the Admin's type decision (the record's own `seen`), then
 * the nomination — two audited acts, each by its own route — and shows both
 * receipts. A refused decision writes nothing; a refused nomination leaves the
 * type confirmed by this Admin, and says so.
 *
 * The preview on screen is always the key in the box. Each preview aborts the
 * one before it, and editing the key aborts the one in flight, so a slow
 * answer for an earlier key can never land under a later one — the card's
 * Confirm posts the card's contract.
 *
 * A refusal the API names (the 409s, the 400 eligibility codes) is explained
 * in this page's words, not `ErrorNotice`'s generic copy for its kind —
 * `NominationProblemNotice`.
 *
 * ## Legal reads it; nothing here changes the contract
 *
 * A sign-in whose roles are known and are not admin sees the page read-only
 * — no form, one sentence saying why. A sign-in that names no role sees the
 * form, and the API's 403 is the answer (D-82-1). Being nominated changes
 * nothing about the contract itself (R-111-1): the record, its scope and its
 * trail are the same, apart from the nomination's own audit rows.
 *
 * ## Every fact is the API's (FR-27)
 *
 * The page formats; it never infers. History is the API's superseded rows,
 * newest first; a practices-scoped reader is told the list covers their scope
 * only, because a base outside it is not shown (R-111-7). So under a practices
 * scope "no base" is "no base visible to you": the preview does not promise
 * version 1 there, and a `base_changed` from that state says a base may be
 * outside the admin's scope rather than "it changed, decide again" — the
 * reload would answer `current: null` again, and the loop would never end.
 */

const ENDPOINT_PATH = ENDPOINTS['list-base-contracts'].path;
const PANEL = 'Base MSA';

function when(instant: string | null): ReactNode {
  return instant === null ? '—' : <time dateTime={instant}>{instant}</time>;
}

function contractLink(row: Pick<BaseContractNomination, 'contract_key' | 'contract_title'>): ReactNode {
  return (
    <>
      <Link to={`/contracts/record/${encodeURIComponent(row.contract_key)}`}>
        <code>{row.contract_key}</code>
      </Link>
      {row.contract_title === null ? null : <span className="mut"> · {row.contract_title}</span>}
    </>
  );
}

const HISTORY_COLUMNS: readonly DataTableColumn<BaseContractNomination>[] = [
  { id: 'version', header: 'Version', cell: (row) => `v${String(row.version_no)}`, cellClassName: 'num' },
  { id: 'contract', header: 'Contract', cell: contractLink },
  {
    id: 'nominated',
    header: 'Nominated',
    cell: (row) => (
      <>
        {row.nominated_by_upn} · {when(row.nominated_utc)}
      </>
    ),
  },
  {
    id: 'superseded',
    header: 'Replaced',
    cell: (row) => (
      <>
        {row.superseded_by_upn ?? '—'} · {when(row.superseded_utc)}
      </>
    ),
  },
  { id: 'note', header: 'Note', cell: (row) => row.note ?? <span className="mut">—</span> },
];

export function BaseMsa() {
  const surface = requireSurface('base-msa');
  const { identity } = useIdentity();
  const mayNominate = mayAttempt(identity, BASE_NOMINATOR_ROLES);
  const { state, reload } = usePanel<ListBaseContractsResponse>((abort) => listBaseContracts(abort), []);
  /*
   * The last list that loaded stays on screen while a reload after a
   * nomination is in flight, so the form — and the outcome it is reporting —
   * is not unmounted by the very reload it asked for. What is shown is still
   * only ever an API answer; the reload replaces it when it lands.
   */
  const [lastReady, setLastReady] = useState<ListBaseContractsResponse | null>(null);
  useEffect(() => {
    if (state.kind === 'ready') setLastReady(state.response);
  }, [state]);
  const shown = state.kind === 'ready' ? state.response : state.kind === 'loading' ? lastReady : null;

  return (
    <>
      <div className="hd">
        <div>
          <h2 id="base-msa-heading">{surface.label}</h2>
          <p>{surface.summary}</p>
          <div className="refs">
            {surface.requirements.map((requirement) => (
              <span key={requirement}>{requirement}</span>
            ))}
          </div>
        </div>
      </div>

      <section className="card qpanel" aria-labelledby="base-msa-heading" aria-busy={state.kind === 'loading'}>
        {state.kind === 'loading' && shown === null ? (
          <p role="status" aria-busy="true">
            Reading {PANEL}…
          </p>
        ) : null}

        {state.kind === 'problem' ? (
          <PanelProblem
            panel={PANEL}
            state={state}
            endpointPath={ENDPOINT_PATH}
            onRetry={reload}
            forbiddenCopy={BASE_MSA_FORBIDDEN_COPY}
          />
        ) : null}

        {shown === null ? null : (
          <>
            {shown.scope_kind === 'practices' ? (
              <p className="banner up" role="status">
                Only nominations whose contract is in your scope are listed. A base outside your scope is not shown here.
              </p>
            ) : null}
            {shown.types.map((entry) => (
              <TypeSection
                key={entry.contract_type}
                entry={entry}
                scoped={shown.scope_kind === 'practices'}
                mayNominate={mayNominate}
                onChanged={reload}
              />
            ))}
          </>
        )}
      </section>
    </>
  );
}

function TypeSection({
  entry,
  scoped,
  mayNominate,
  onChanged,
}: {
  readonly entry: BaseContractTypeEntry;
  readonly scoped: boolean;
  readonly mayNominate: boolean;
  readonly onChanged: () => void;
}) {
  const type = entry.contract_type;
  return (
    <div data-testid={`base-${type.toLowerCase()}`}>
      <h3>Current base {type}</h3>
      {entry.current === null ? (
        <p className="mut" data-testid="base-none">
          No base {type} has been nominated{scoped ? ' in your scope' : ''}.
        </p>
      ) : (
        <CurrentNomination type={type} nomination={entry.current} />
      )}

      {mayNominate ? (
        <NominateForm type={type} current={entry.current} scoped={scoped} onChanged={onChanged} />
      ) : (
        <p className="mut" data-testid="base-read-only">
          {BASE_MSA_READ_ONLY_COPY}
        </p>
      )}

      <h3>Earlier versions</h3>
      <DataTable
        caption={`Earlier base ${type} versions, newest first`}
        columns={HISTORY_COLUMNS}
        rows={entry.history}
        rowKey={(row) => row.nomination_id}
        empty={<p className="mut">No earlier version{entry.current === null ? '' : ' — the current base is the first'}.</p>}
      />
    </div>
  );
}

function CurrentNomination({ type, nomination }: { readonly type: string; readonly nomination: BaseContractNomination }) {
  return (
    <div className="frows" data-testid="base-current">
      <div className="frow-f">
        <span className="fname">Contract</span>
        <span className="fval">
          {contractLink(nomination)}{' '}
          <span className="tag acc">
            Base {type} · v{nomination.version_no}
          </span>
        </span>
      </div>
      <div className="frow-f">
        <span className="fname">Lifecycle</span>
        <span className="fval">
          <span className="tag up">{nomination.lifecycle_status}</span>
          {nomination.type_mismatch ? (
            <>
              {' '}
              <span className="tag bad" title="The contract’s type was corrected after it was nominated; the nomination stays until an admin replaces it.">
                No longer an {type} — type corrected since nomination
              </span>
            </>
          ) : null}
        </span>
      </div>
      <div className="frow-f">
        <span className="fname">Nominated</span>
        <span className="fval">
          {nomination.nominated_by_upn} · {when(nomination.nominated_utc)}
        </span>
      </div>
      <div className="frow-f">
        <span className="fname">Note</span>
        <span className="fval" data-absent={nomination.note === null ? 'true' : 'false'}>
          {nomination.note ?? 'No note was given'}
        </span>
      </div>
    </div>
  );
}

type Preview =
  | { readonly kind: 'none' }
  | { readonly kind: 'loading'; readonly key: string }
  | { readonly kind: 'ready'; readonly contract: ContractDetail }
  | { readonly kind: 'problem'; readonly problem: ApiProblemKind; readonly correlation_id: string | null };

function NominateForm({
  type,
  current,
  scoped,
  onChanged,
}: {
  readonly type: BaseContractType;
  readonly current: BaseContractNomination | null;
  /** The list is a practices scope's: `current: null` is "none visible", not "none". */
  readonly scoped: boolean;
  readonly onChanged: () => void;
}) {
  const [key, setKey] = useState('');
  const [note, setNote] = useState('');
  const [keyInvalid, setKeyInvalid] = useState(false);
  const [preview, setPreview] = useState<Preview>({ kind: 'none' });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [problem, setProblem] = useState<NominationProblem | null>(null);
  /* CLM-118 — Confirm type and set as base: the type decision's receipt, or why it was refused. */
  const [typeConfirmed, setTypeConfirmed] = useState<DecideContractTypeResponse | null>(null);
  const [decisionProblem, setDecisionProblem] = useState<DecisionProblem | null>(null);
  /* The preview in flight. Replaced by the next one and aborted by a key edit, so only the box's key can land. */
  const previewAbort = useRef<AbortController | null>(null);
  useEffect(() => () => previewAbort.current?.abort(), []);
  const scopedWithoutBase = scoped && current === null;

  function dropPreview() {
    previewAbort.current?.abort();
    previewAbort.current = null;
    setPreview({ kind: 'none' });
  }

  async function load() {
    const trimmed = key.trim();
    setDone(null);
    setProblem(null);
    setTypeConfirmed(null);
    setDecisionProblem(null);
    dropPreview();
    if (!ContractKeySchema.safeParse(trimmed).success) {
      setKeyInvalid(true);
      return;
    }
    setKeyInvalid(false);
    const controller = new AbortController();
    previewAbort.current = controller;
    setPreview({ kind: 'loading', key: trimmed });
    try {
      const response = await retrieveContract(trimmed, undefined, controller.signal);
      if (controller.signal.aborted) return;
      setPreview({ kind: 'ready', contract: response.contract });
    } catch (error) {
      if (controller.signal.aborted) return;
      setPreview({
        kind: 'problem',
        problem: classifyApiError(error),
        correlation_id: error instanceof ApiError ? error.correlation_id : null,
      });
    }
  }

  async function confirm(contract: ContractDetail) {
    setSaving(true);
    setProblem(null);
    try {
      const trimmedNote = note.trim();
      const response = await nominateBaseContract({
        contract_type: type,
        contract_key: contract.contract_key,
        expected_current_nomination_id: current?.nomination_id ?? null,
        ...(trimmedNote === '' ? {} : { note: trimmedNote }),
      });
      setDone(`${contract.contract_key} is now the base ${type} (v${String(response.nomination.version_no)}).`);
      setPreview({ kind: 'none' });
      setKey('');
      setNote('');
      onChanged();
    } catch (error) {
      const failure = nominationProblemOf(error, { scopedWithoutBase });
      setProblem(failure);
      /* Somebody else moved the base — or one outside this scope holds it: show what is visible now. */
      if (failure.refusal?.code === 'base_changed') onChanged();
    } finally {
      setSaving(false);
    }
  }

  /** CLM-118 (R-118-13) — the Admin's type decision, then the nomination; both receipts are shown. */
  async function confirmThenNominate(contract: ContractDetail) {
    setSaving(true);
    setProblem(null);
    setDecisionProblem(null);
    setTypeConfirmed(null);
    try {
      const trimmedNote = note.trim();
      const outcome = await confirmTypeAndSetBase({
        type,
        contract,
        expected_current_nomination_id: current?.nomination_id ?? null,
        ...(trimmedNote === '' ? {} : { note: trimmedNote }),
        scopedWithoutBase,
      });
      if (outcome.kind === 'decision_failed') {
        setDecisionProblem(outcome.problem);
        return;
      }
      setTypeConfirmed(outcome.decision);
      setPreview({ kind: 'none' });
      if (outcome.kind === 'nomination_failed') {
        setProblem(outcome.problem);
        if (outcome.problem.refusal?.code === 'base_changed') onChanged();
        return;
      }
      setDone(
        `${contract.contract_key} is now the base ${type} (v${String(outcome.nomination.nomination.version_no)}).`,
      );
      setKey('');
      setNote('');
      onChanged();
    } finally {
      setSaving(false);
    }
  }

  const verb = current === null ? `Nominate a contract as the base ${type}` : `Replace the base ${type}`;

  return (
    <div className="vform" role="group" aria-label={verb} data-testid="base-nominate">
      <h4>{verb}</h4>
      <div className="field">
        <label htmlFor={`base-${type}-key`}>Contract key</label>
        <input
          id={`base-${type}-key`}
          type="text"
          value={key}
          disabled={saving}
          placeholder="e.g. SGW-0012 or SGW-MSA-0003"
          onChange={(event) => {
            setKey(event.target.value);
            dropPreview();
          }}
        />
      </div>
      <div className="field">
        <label htmlFor={`base-${type}-note`}>Note (optional)</label>
        <textarea
          id={`base-${type}-note`}
          value={note}
          disabled={saving}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
      <div className="vacts">
        <button type="button" className="btn" disabled={saving || key.trim() === ''} onClick={() => void load()}>
          Preview contract
        </button>
      </div>
      {keyInvalid ? (
        <p className="banner up" role="status">
          That is not a contract key. Keys look like <code>SGW-0012</code>, or <code>SGW-MSA-0003</code> for one
          issued before 1 Oct 2026.
        </p>
      ) : null}
      {preview.kind === 'loading' ? (
        <p role="status" aria-busy="true">
          Reading <code>{preview.key}</code>…
        </p>
      ) : null}
      {preview.kind === 'problem' ? <ErrorNotice kind={preview.problem} correlation_id={preview.correlation_id} /> : null}
      {preview.kind === 'ready' ? (
        <PreviewCard
          type={type}
          contract={preview.contract}
          current={current}
          scopedWithoutBase={scopedWithoutBase}
          saving={saving}
          onConfirm={() => void confirm(preview.contract)}
          onConfirmTypeFirst={() => void confirmThenNominate(preview.contract)}
          onCancel={dropPreview}
        />
      ) : null}
      {typeConfirmed === null ? null : (
        <p role="status" data-testid="base-type-confirmed">
          <strong>{receiptLine(typeConfirmed)}</strong>
          {done === null ? (
            <span className="mut"> The type stays confirmed by you; the nomination was not recorded.</span>
          ) : null}
        </p>
      )}
      {done === null ? null : (
        <p role="status">
          <strong>{done}</strong>
        </p>
      )}
      {decisionProblem === null ? null : (
        <DecisionProblemNotice problem={decisionProblem} onReload={() => void load()} />
      )}
      {problem === null ? null : <NominationProblemNotice problem={problem} />}
    </div>
  );
}

function PreviewCard({
  type,
  contract,
  current,
  scopedWithoutBase,
  saving,
  onConfirm,
  onConfirmTypeFirst,
  onCancel,
}: {
  readonly type: BaseContractType;
  readonly contract: ContractDetail;
  readonly current: BaseContractNomination | null;
  readonly scopedWithoutBase: boolean;
  readonly saving: boolean;
  readonly onConfirm: () => void;
  /** CLM-118 (R-118-13) — confirm the type, then nominate. */
  readonly onConfirmTypeFirst: () => void;
  readonly onCancel: () => void;
}) {
  const ineligible = baseContractIneligibility(type, baseContractCandidateOf(contract));
  const alreadyCurrent = current !== null && current.contract_key === contract.contract_key;
  /* An MSA the file name and the AI agree on is a base once an administrator confirms its type. */
  const typeFirst = !alreadyCurrent && confirmsTypeFirst(type, contract);
  const blocked = (ineligible !== null && !typeFirst) || alreadyCurrent;
  const parties = contract.parties.map((party) => `${party.cached_display_name} (${party.party_role})`).join(', ');

  return (
    <div className="card" data-testid="base-preview">
      <p>
        <code>{contract.contract_key}</code> · {parties} ·{' '}
        <ContractTypeTag
          type={contract.contract_type}
          confirmation={contract.contract_type_status.confirmation}
          autoKind={contract.contract_type_status.auto_confirmation?.kind ?? null}
        />{' '}
        ·{' '}
        <span className="tag up">{contract.lifecycle_status}</span>
        {contract.processing_restricted ? (
          <>
            {' '}
            <span className="tag bad">Processing restricted</span>
          </>
        ) : null}
      </p>
      {ineligible === null ? null : (
        <p className={`banner ${typeFirst ? 'up' : 'bad'}`} role="status" data-testid="base-ineligible">
          {INELIGIBLE_COPY[ineligible]}
        </p>
      )}
      {alreadyCurrent ? (
        <p className="banner up" role="status">
          This contract is already the current base {type}.
        </p>
      ) : null}
      {blocked ? null : (
        <p className="mut">
          {current === null
            ? scopedWithoutBase
              ? scopedWithoutBaseCopy(type)
              : `This contract becomes the base ${type}, version 1.`
            : `This contract replaces ${current.contract_key} (v${String(current.version_no)}) as the base ${type}. The earlier version stays in the history, and reports run against it stay pinned to it.`}{' '}
          Being nominated changes nothing about the contract itself.
        </p>
      )}
      <div className="vacts">
        <button
          type="button"
          className="btn pri"
          disabled={saving || blocked}
          onClick={typeFirst ? onConfirmTypeFirst : onConfirm}
        >
          {saving
            ? 'Saving…'
            : typeFirst
              ? current === null
                ? 'Confirm type and set as base'
                : `Confirm type and replace v${String(current.version_no)}`
              : current === null
                ? `Nominate as base ${type}`
                : `Replace v${String(current.version_no)} with this contract`}
        </button>
        <button type="button" className="btn qt" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
